// Вид грида на пользователя: порядок колонок и скрытые колонки.
//
// Был только у задач и жил в localStorage: хук был завязан на `TaskColumnKey`,
// а настройка не переезжала на другой компьютер. Теперь хук общий для всех
// гридов, а профиль хранится на сервере (`GET/PUT/DELETE /grid-profiles`).
//
// Набор колонок знает только клиент, поэтому нормализация — здесь: из ответа
// берутся лишь существующие ключи, а колонки, появившиеся после сохранения
// профиля, дописываются в конец. Иначе новая колонка не показалась бы никому,
// кто уже трогал настройки.
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../scope/useCurrentScope.js';
import {
  getGridProfilesQueryKey,
  listGridProfiles,
  resetGridProfile,
  saveGridProfile,
  type GridProfileItem,
} from './gridProfiles.client.js';

export interface GridColumnMeta {
  key: string;
  /** Подпись в меню «Колонки». */
  label: string;
  /** Скрыта по умолчанию — включается в меню. */
  hiddenByDefault?: boolean;
}

export interface GridProfile {
  /** Полный порядок колонок, включая скрытые. */
  order: string[];
  hidden: string[];
  /** Что показывать: порядок без скрытых. */
  visibleOrder: string[];
  columns: GridColumnMeta[];
  moveColumn: (from: string, to: string) => void;
  toggleColumn: (key: string) => void;
  reset: () => void;
}

/** Порядок из профиля + колонки, появившиеся после его сохранения. */
function normalize(columns: readonly GridColumnMeta[], stored?: GridProfileItem): GridProfile {
  const known = new Set(columns.map((column) => column.key));
  const storedOrder = (stored?.order ?? []).filter((key) => known.has(key));
  const missing = columns.map((c) => c.key).filter((key) => !storedOrder.includes(key));
  const order = [...storedOrder, ...missing];

  const hidden = stored
    ? (stored.hidden ?? []).filter((key) => known.has(key))
    : columns.filter((column) => column.hiddenByDefault).map((column) => column.key);

  return {
    order,
    hidden,
    visibleOrder: order.filter((key) => !hidden.includes(key)),
    columns: [...columns],
    moveColumn: () => undefined,
    toggleColumn: () => undefined,
    reset: () => undefined,
  };
}

/**
 * @param grid ключ грида: `tasks`, `contacts`, … — он же ключ строки в БД.
 * @param columns список колонок, которыми можно управлять (без «Действий»).
 */
export function useGridProfile(grid: string, columns: readonly GridColumnMeta[]): GridProfile {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  const queryKey = getGridProfilesQueryKey(workspaceId);

  const profilesQuery = useQuery({
    queryKey,
    queryFn: listGridProfiles,
    enabled: scope !== null,
    // Вид грида меняется редко и только этим же пользователем.
    staleTime: 5 * 60_000,
  });

  const stored = useMemo(
    () => (profilesQuery.data ?? []).find((item) => item.grid === grid),
    [profilesQuery.data, grid],
  );

  const state = useMemo(() => normalize(columns, stored), [columns, stored]);

  /** Оптимистично правим кэш: грид не должен ждать ответа сервера. */
  const writeToCache = useCallback(
    (next: { order: string[]; hidden: string[] }) => {
      queryClient.setQueryData<GridProfileItem[]>(queryKey, (current) => {
        const rest = (current ?? []).filter((item) => item.grid !== grid);
        return [...rest, { grid, order: next.order, hidden: next.hidden }];
      });
    },
    [grid, queryClient, queryKey],
  );

  const saveMutation = useMutation({
    mutationFn: (next: { order: string[]; hidden: string[] }) => saveGridProfile(grid, next),
    // Ответ сервера — тот же профиль; перечитываем на случай гонки двух вкладок.
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const resetMutation = useMutation({
    mutationFn: () => resetGridProfile(grid),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const persist = useCallback(
    (next: { order: string[]; hidden: string[] }) => {
      writeToCache(next);
      saveMutation.mutate(next);
    },
    [saveMutation, writeToCache],
  );

  const moveColumn = useCallback(
    (from: string, to: string) => {
      // Работаем с ключами, а не индексами: часть колонок бывает скрыта или
      // подавлена контекстом, и индексы грида не совпадают с индексами профиля.
      if (from === to) return;
      const fromIndex = state.order.indexOf(from);
      const toIndex = state.order.indexOf(to);
      if (fromIndex === -1 || toIndex === -1) return;

      const order = state.order.filter((key) => key !== from);
      order.splice(order.indexOf(to) + (toIndex > fromIndex ? 1 : 0), 0, from);
      persist({ order, hidden: state.hidden });
    },
    [persist, state.hidden, state.order],
  );

  const toggleColumn = useCallback(
    (key: string) => {
      const hidden = state.hidden.includes(key)
        ? state.hidden.filter((item) => item !== key)
        : [...state.hidden, key];
      persist({ order: state.order, hidden });
    },
    [persist, state.hidden, state.order],
  );

  const reset = useCallback(() => {
    queryClient.setQueryData<GridProfileItem[]>(queryKey, (current) =>
      (current ?? []).filter((item) => item.grid !== grid),
    );
    resetMutation.mutate();
  }, [grid, queryClient, queryKey, resetMutation]);

  return { ...state, moveColumn, toggleColumn, reset };
}
